import React, { useState, useCallback } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';
import { ExternalAgreement } from '@/types';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { ValidationForm } from '@/components/forms/validation';
import { Button } from '@/components/Buttons';
import { Trans } from '@lingui/react/macro';
import { PriceRowEditor, PriceRow, PriceRowEditorFooter } from '@/seller/components/PriceRow';
import { useAsyncState } from '@/utils/hooks';
import { getProjectFeeSets } from '@/services/fees';
import { NoAvailableFeesAlert } from '@/seller/components/fees';
import { arrayReplace } from '@ygtechnologies/rubus-frontend-utilities';
import { updateExternalAgreement } from '@/services/external-agreements';
import { useSelector } from 'react-redux';
import { IState } from '@/state/IState';

export interface ExternalAgreementEditPriceDialogProps {
    externalAgreement: ExternalAgreement;
    show: boolean;
    onClose(): void;
    onSave(externalAgreement: ExternalAgreement): void;
}
const ExternalAgreementEditPriceDialog: React.FC<ExternalAgreementEditPriceDialogProps> = ({
    externalAgreement,
    show,
    onClose,
    onSave,
}) => {
    const [saving, setSaving] = useState(false);
    const [projectFeeSets] = useAsyncState(
        () => getProjectFeeSets(externalAgreement.projectId),
        [externalAgreement.projectId],
        null
    );
    const [lines, setLines] = useState<ExtendedPriceRow[]>([]);
    const activeProject = useSelector((state: IState) => state.global.activeProject!);

    const onOpen = () => {
        setLines(
            externalAgreement.lines.map((line) => {
                return {
                    id: line.id,
                    price: line.priceCalculation?.basePrice ?? null,
                    description: line.description,
                    projectFeeSetId: line.projectFeeSetId,
                    itemCategoryId: line.itemCategoryId,
                    tags: line.tags,
                };
            })
        );
    };

    const handleChange = (newItem: PriceRow, oldItem: PriceRow) => {
        setLines(arrayReplace(lines, oldItem, newItem));
    };

    const handleAdd = useCallback(() => {
        const defaultFeeSet = projectFeeSets!.find((x) => x.isDefault) || projectFeeSets![0];
        setLines([
            ...lines,
            {
                description: '',
                price: null,
                projectFeeSetId: defaultFeeSet ? defaultFeeSet.id : 0,
                itemCategoryId: undefined,
                tags: [],
            },
        ]);
    }, [projectFeeSets, lines]);

    const handleRemove = (item: PriceRow) => {
        setLines(lines.filter((x) => x !== item));
    };
    const handleCancelClick = () => {
        triggerHandler(onClose);
    };

    const handleAcceptClick = async (e) => {
        e.preventDefault();
        setSaving(true);
        var ea = await updateExternalAgreement(externalAgreement.id, {
            lines: lines,
        });
        setSaving(false);
        onSave(ea);
    };

    const renderContent = () => {
        if (!projectFeeSets) {
            return (
                <ModalBody>
                    <LoadingIndicator />
                </ModalBody>
            );
        }

        if (projectFeeSets.length === 0) {
            return (
                <>
                    <ModalBody>
                        <NoAvailableFeesAlert />
                    </ModalBody>
                    <ModalFooter>
                        <Button color="tertiary" onClick={handleCancelClick} disabled={saving}>
                            <Trans>Close</Trans>
                        </Button>
                    </ModalFooter>
                </>
            );
        }

        return (
            <>
                <ModalBody>
                    {lines.map((line, i) => (
                        <PriceRowEditor
                            key={i}
                            showLabels={i === 0}
                            projectFeeSets={projectFeeSets}
                            value={line}
                            onChange={(newValue) => handleChange({ ...line, ...newValue }, line)}
                            onDeleteClick={() => handleRemove(line)}
                            rootItemCategoryFilter={(cat) =>
                                activeProject.permissions.categoryPermissions[cat.id]?.canUseInChangeRequests ?? false
                            }
                        />
                    ))}
                    <PriceRowEditorFooter rows={lines} onAddRowClick={handleAdd} />
                </ModalBody>
                <ModalFooter>
                    <Button color="tertiary" onClick={handleCancelClick} disabled={saving}>
                        <Trans>Cancel</Trans>
                    </Button>
                    <Button type="submit" color="primary" loading={saving}>
                        <Trans>Save</Trans>
                    </Button>
                </ModalFooter>
            </>
        );
    };

    return (
        <Modal isOpen={show} onOpen={onOpen} toggle={handleCancelClick} size="xl">
            <ValidationForm onSubmit={handleAcceptClick}>
                <ModalHeader>
                    <Trans>Edit pricing</Trans>
                </ModalHeader>
                {renderContent()}
            </ValidationForm>
        </Modal>
    );
};

interface ExtendedPriceRow extends PriceRow {
    id?: number;
}

export default ExternalAgreementEditPriceDialog;